import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Input,
  Select,
  Switch,
  InputNumber,
  message,
  Tabs,
  Space,
  Tag,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { updateWidget, UpdateWidgetRequest, Widget, WidgetAppearance, WidgetSettings, getProjectsForWidget } from '../../lib/widgetsApi';

const { TextArea } = Input;
const { TabPane } = Tabs;

interface EditWidgetModalProps {
  visible: boolean;
  widget: Widget;
  onClose: () => void;
  onSuccess: () => void;
}

const EditWidgetModal: React.FC<EditWidgetModalProps> = ({
  visible,
  widget,
  onClose,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [projects, setProjects] = useState<any[]>([]);
  const [allowedDomains, setAllowedDomains] = useState<string[]>(widget.allowedDomains || []);
  const [domainInput, setDomainInput] = useState('');

  useEffect(() => {
    if (visible) {
      loadProjects();
      // Set form values
      form.setFieldsValue({
        name: widget.name,
        description: widget.description,
        projectId: widget.projectId,
        isActive: widget.isActive,
        ...widget.appearance,
        ...widget.settings,
      });
      setAllowedDomains(widget.allowedDomains || []);
    }
  }, [visible, widget]);

  const loadProjects = async () => {
    try {
      const data = await getProjectsForWidget();
      setProjects(data);
    } catch (error: any) {
      message.error('Failed to load projects');
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const appearance: WidgetAppearance = {
        primaryColor: values.primaryColor,
        secondaryColor: values.secondaryColor,
        position: values.position,
        size: values.size,
        welcomeMessage: values.welcomeMessage,
        placeholder: values.placeholder,
        autoOpen: values.autoOpen || false,
        showBranding: values.showBranding !== false,
      };

      const settings: WidgetSettings = {
        maxMessagesHistory: values.maxMessagesHistory || 50,
        rateLimitPerMin: values.rateLimitPerMin || 10,
        collectUserInfo: values.collectUserInfo || false,
        enableFeedback: values.enableFeedback !== false,
      };

      const request: UpdateWidgetRequest = {
        name: values.name,
        description: values.description,
        projectId: values.projectId,
        isActive: values.isActive,
        appearance,
        settings,
        allowedDomains,
      };

      await updateWidget(widget.id, request);
      message.success('Widget updated successfully');
      onSuccess();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to update widget');
    } finally {
      setLoading(false);
    }
  };

  const handleAddDomain = () => {
    if (domainInput && !allowedDomains.includes(domainInput)) {
      setAllowedDomains([...allowedDomains, domainInput]);
      setDomainInput('');
    }
  };

  const handleRemoveDomain = (domain: string) => {
    setAllowedDomains(allowedDomains.filter((d) => d !== domain));
  };

  return (
    <Modal
      title="Edit Chat Widget"
      open={visible}
      onCancel={onClose}
      onOk={handleSubmit}
      confirmLoading={loading}
      width={700}
      okText="Save Changes"
    >
      <Form form={form} layout="vertical">
        <Tabs defaultActiveKey="basic">
          <TabPane tab="Basic Info" key="basic">
            <Form.Item
              label="Widget Name"
              name="name"
              rules={[{ required: true, message: 'Please enter widget name' }]}
            >
              <Input placeholder="e.g., Customer Support Chat" />
            </Form.Item>

            <Form.Item label="Description" name="description">
              <TextArea rows={2} placeholder="Optional description" />
            </Form.Item>

            <Form.Item
              label="Project"
              name="projectId"
              rules={[{ required: true, message: 'Please select a project' }]}
            >
              <Select placeholder="Select project">
                {projects.map((project) => (
                  <Select.Option key={project.id} value={project.id}>
                    {project.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item label="Widget Status" name="isActive" valuePropName="checked">
              <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
            </Form.Item>

            <Form.Item label="Allowed Domains">
              <Space direction="vertical" style={{ width: '100%' }}>
                <Input.Search
                  placeholder="example.com"
                  value={domainInput}
                  onChange={(e) => setDomainInput(e.target.value)}
                  onSearch={handleAddDomain}
                  enterButton={<PlusOutlined />}
                />
                <div>
                  {allowedDomains.map((domain) => (
                    <Tag
                      key={domain}
                      closable
                      onClose={() => handleRemoveDomain(domain)}
                    >
                      {domain}
                    </Tag>
                  ))}
                  {allowedDomains.length === 0 && (
                    <span className="text-gray-400 text-sm">
                      All domains allowed (not recommended for production)
                    </span>
                  )}
                </div>
              </Space>
            </Form.Item>
          </TabPane>

          <TabPane tab="Appearance" key="appearance">
            <Form.Item label="Primary Color" name="primaryColor">
              <Input type="color" style={{ width: '100px' }} />
            </Form.Item>

            <Form.Item label="Secondary Color" name="secondaryColor">
              <Input type="color" style={{ width: '100px' }} />
            </Form.Item>

            <Form.Item label="Position" name="position">
              <Select>
                <Select.Option value="bottom-right">Bottom Right</Select.Option>
                <Select.Option value="bottom-left">Bottom Left</Select.Option>
                <Select.Option value="top-right">Top Right</Select.Option>
                <Select.Option value="top-left">Top Left</Select.Option>
              </Select>
            </Form.Item>

            <Form.Item label="Size" name="size">
              <Select>
                <Select.Option value="small">Small</Select.Option>
                <Select.Option value="medium">Medium</Select.Option>
                <Select.Option value="large">Large</Select.Option>
              </Select>
            </Form.Item>

            <Form.Item label="Welcome Message" name="welcomeMessage">
              <Input placeholder="Hi! How can I help you today?" />
            </Form.Item>

            <Form.Item label="Input Placeholder" name="placeholder">
              <Input placeholder="Type your message..." />
            </Form.Item>

            <Form.Item label="Auto-Open Widget" name="autoOpen" valuePropName="checked">
              <Switch />
            </Form.Item>

            <Form.Item label="Show ThiranAI Branding" name="showBranding" valuePropName="checked">
              <Switch />
            </Form.Item>
          </TabPane>

          <TabPane tab="Settings" key="settings">
            <Form.Item label="Max Message History" name="maxMessagesHistory">
              <InputNumber min={10} max={200} style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item label="Rate Limit (per minute)" name="rateLimitPerMin">
              <InputNumber min={1} max={60} style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item
              label="Collect User Information"
              name="collectUserInfo"
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>

            <Form.Item label="Enable Feedback" name="enableFeedback" valuePropName="checked">
              <Switch />
            </Form.Item>
          </TabPane>
        </Tabs>
      </Form>
    </Modal>
  );
};

export default EditWidgetModal;
